import React, { useState, useMemo } from 'react'
import CommonCard from '../../../../global/card'
import { useLocation } from "react-router-dom"
import SOURCES from '../../../../../utils/constants'
import useDebounce from '../../../../../utils/customHooks'
import { useGenericQuery } from '../../../../../queries/use-generic-query'
import { QUERY_KEYS } from '../../../../../lib/query-constants'
import EnhancedCancelledSubscriptionsList from './enhancedCancelledSubscriptionsList'
import CommonPagination from '../../../../global/pagination'
import CommonSkeletonLoader from '../../../../global/loading'
import CommonCardView from '../../../../global/cardView'
import EnhancedCancelledSubscriptionsCardView from './enhancedCancelledSubscriptionsCardView'
import NoRecordFound from '../../../../global/noRecordFound'

const EnhancedCancelledSubscriptions = () => {
    const [cancelledSubscriptionsSearch, setCancelledSubscriptionsSearch] = useState("")
    const [cancelledSubscriptionsViewType, setCancelledSubscriptionsViewType] = useState(() => {
        return localStorage.getItem("cancelledSubscriptionsViewType") || "GRID";
    });
    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
    const limitFromQuery = parseInt(
        searchParams.get("limit") || SOURCES.DEFAULT_RECORDS_LISTING,
        24
    );
    const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);

    const [pagination, setPagination] = useState({
        offset: offsetFromQuery,
        limit: limitFromQuery,
        page: pageFromQuery,
    });

    const keyParams = useMemo(() => ({
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    }), [pagination.offset, pagination.limit, sort.sortType, sort.sortOrder]);

    const debouncedSearchTerm = useDebounce(cancelledSubscriptionsSearch, 500);

    const { data, isLoading } = useGenericQuery({
        endpoint: "/admin/subscription/cancelled",
        queryKey: QUERY_KEYS.cancelledSubscriptions.lists({ ...keyParams }),
        queryParams: { ...keyParams },
        enabled: !debouncedSearchTerm
    })

    const { data: searchData, isInitialLoading: searchDataLoading } = useGenericQuery({
        endpoint: "/admin/subscription/cancelled/search",
        queryKey: QUERY_KEYS.cancelledSubscriptions.lists({ q: debouncedSearchTerm }),
        queryParams: { q: debouncedSearchTerm },
        enabled: !!debouncedSearchTerm && debouncedSearchTerm.length >= 2
    })

    const isLoadingList = isLoading || searchDataLoading;

    const finalizedData = useMemo(() => {
        if (debouncedSearchTerm && searchData) {
            return searchData;
        }
        return data;
    }, [data, searchData, debouncedSearchTerm]);


    return (
        <CommonCard
            isSearch={{
                show: true,
                placeholder: "Search cancelled subscriptions.",
                searchInput: cancelledSubscriptionsSearch,
                setSearchInput: setCancelledSubscriptionsSearch
            }}
            isListGridView={{
                show: true,
                setViewType: setCancelledSubscriptionsViewType,
                viewType: cancelledSubscriptionsViewType,
                viewFor: "cancelledSubscriptionsViewType"
            }}
            isLoading={{
                show: isLoadingList,
                loaderCmp: <CommonSkeletonLoader type={cancelledSubscriptionsViewType === "LIST" ? 'table' : 'card'} />
            }}
        >
            {finalizedData?.logs?.length > 0 ? (
                <>
                    {cancelledSubscriptionsViewType === "LIST" ? (
                        <EnhancedCancelledSubscriptionsList
                            subscriptionList={finalizedData}
                            isLoading={isLoadingList}
                            sort={sort}
                            setSort={setSort}
                        />
                    ) : (
                        <CommonCardView>
                            {finalizedData.logs.map((record, idx) => (
                                <EnhancedCancelledSubscriptionsCardView
                                    key={record._id || idx}
                                    record={record}
                                />
                            ))}
                        </CommonCardView>
                    )}
                </>
            ) : (
                !isLoadingList && <NoRecordFound message="No cancelled subscriptions found." />
            )}

            {
                !debouncedSearchTerm &&
                finalizedData?.totalRecords > SOURCES.DEFAULT_RECORDS_LISTING && (
                    <CommonPagination
                        currentPage={pagination.page}
                        defaultLimit={pagination.limit}
                        totalRecords={finalizedData?.totalRecords}
                        setCurrentPage={setPagination}
                        activePageRecords={finalizedData?.logs?.length || 0}
                        paginationFor="Cancelled Subscriptions"
                        className="mt-4"
                    />
                )
            }
        </CommonCard>
    )
}

export default EnhancedCancelledSubscriptions